import { once } from 'lodash-es'
import { Model, type ModelProps } from '@/infra/model'
import { observable, computed } from 'mobx'
import { Job } from '@/features/jobs/Job'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'

export type JobScopeChangeStatus =
  | 'Accepted'
  | 'Rejected'
  | 'PendingFinalization'
  | 'PendingAdminApproval'
  | 'PendingClientApproval'
  | 'ApplyFailed'
  | 'Unspecified'

export interface JobScopeChangeItem {
  id: string
  name: string
  quantity: number
}

export type JobScopeChangeProps = ModelProps<JobScopeChange>

export class JobScopeChange extends Model<JobScopeChange> {
  id = Id.none

  /**
   * Default scope change request.
   */
  static get none() {
    return lazyNone()
  }

  @observable accessor job = Job.none
  @observable accessor reason = ''
  @observable accessor status: JobScopeChangeStatus = 'PendingFinalization'
  @observable accessor items: JobScopeChangeItem[] = []
  @observable accessor createTime: Date = unixEpoch()
  @observable accessor rejectionReason = ''
  @observable accessor createdByUserPublicId = Id.none
  @observable accessor latestResponseTime: Date | null = null

  /**
   * Whether the scope change was accepted.
   */
  @computed
  get isAccepted() {
    return this.status === 'Accepted'
  }

  /**
   * Whether the scope change was rejected.
   */
  @computed
  get isRejected() {
    return this.status === 'Rejected'
  }

  /**
   * Whether applying the accepted scope change failed.
   */
  @computed
  get isApplyFailed() {
    return this.status === 'ApplyFailed'
  }

  /**
   * Whether the scope change awaits a response.
   */
  @computed
  get isPending() {
    return (
      this.status !== 'Accepted' &&
      this.status !== 'Rejected' &&
      this.status !== 'ApplyFailed'
    )
  }

  /**
   * Whether an admin, rather than a collaborator, created the scope change.
   */
  @computed
  get isCreatedByAdmin() {
    const createdByCollaborator = this.job.collaborators.find(
      (c) => c.member.user.publicId === this.createdByUserPublicId,
    )
    return createdByCollaborator === undefined
  }
}

const lazyNone = once(() => new JobScopeChange())
